import { ReviewPageLayout } from '../components/ReviewPageLayout/ReviewPageLayout';
import type { PageProps, ReviewPayload } from '../type';
import { Stack, Text } from 'braid-design-system';

import type { Rating } from '../components/StarSelector/StarSelector';
import { CareerOpportunityRatingQuestion } from '../questions/RatingQuesions/CareerOpportunityRatingQuestion';
import { WorkLifeBalanceRatingQuestion } from '../questions/RatingQuesions/WorkLifeBalanceRatingQuestion';
import { WorkEnvironmentRatingQuestion } from '../questions/RatingQuesions/WorkEnvironmentRatingQuestion';
import { DiversityRatingQuestion } from '../questions/RatingQuesions/DiversityRatingQuestion';
import { PerksAndBenefitsRatingQuestion } from '../questions/RatingQuesions/PerksAndBenefitsRatingQuestion';
import { ExecutiveManagementRatingQuestion } from '../questions/RatingQuesions/ExecutiveManagementRatingQuestion';
import type { ValidationError } from '@seek/libs-shared';

type Props = PageProps & {
  formErrors?: ValidationError[];
};

export const YourFeedbackPage = ({
  companyProfile,
  review,
  setter,
  setPage,
  onSubmit,
  pageNumber,
  formErrors,
}: Props) => {
  const handleOnChange =
    (updateProperty: keyof ReviewPayload) => (value: Rating) => {
      setter({
        ...review,
        [updateProperty]: value,
      });
    };

  const handleOnContinue = () => {
    if (onSubmit) {
      onSubmit();
    }
  };

  const handleOnBack = () => {
    setPage(pageNumber - 1);
  };

  // This should never happen, but if it does, we want to show the user
  const formErrorComponent = formErrors && formErrors.length > 0 && (
    <Stack space="small">
      <Text tone="critical" weight="strong">
        Could not submit review due to the following errors:
      </Text>
      {formErrors.map((error) => (
        <Text key={error.fieldName} tone="critical">
          {`${error.fieldName} - ${error.message}`}
        </Text>
      ))}
    </Stack>
  );

  return (
    <ReviewPageLayout
      progress={pageNumber}
      companyName={companyProfile?.name}
      onContinue={handleOnContinue}
      onBack={handleOnBack}
      testid="ratings-page"
    >
      <Stack space="large">
        <CareerOpportunityRatingQuestion
          onChange={handleOnChange('careerOpportunityRating')}
          value={review?.careerOpportunityRating}
        />
        <WorkLifeBalanceRatingQuestion
          onChange={handleOnChange('workLifeBalanceRating')}
          value={review?.workLifeBalanceRating}
        />
        <WorkEnvironmentRatingQuestion
          onChange={handleOnChange('workEnvironmentRating')}
          value={review?.workEnvironmentRating}
        />
        <ExecutiveManagementRatingQuestion
          onChange={handleOnChange('executiveManagementRating')}
          value={review?.executiveManagementRating}
        />
        <PerksAndBenefitsRatingQuestion
          onChange={handleOnChange('perksAndBenefitsRating')}
          value={review?.perksAndBenefitsRating}
        />
        <DiversityRatingQuestion
          onChange={handleOnChange('diversityRating')}
          value={review?.diversityRating}
        />
        {formErrorComponent}
      </Stack>
    </ReviewPageLayout>
  );
};
